import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { ArrowDown } from "components/icons/ArrowDown";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReportsType } from "pages/Books/Reports/Reports";
import { useState } from "react";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { useExportVarianceExplanationReportMutation } from "store/apis/reports";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { ReportFilterType } from "../ReportFilter";
import { ExportFluxAnalysisModal } from "./ExportFluxAnalysisModal";

const PENDING_EXPORT_ACTION = {
  MAIL: "MAIL",
  LOCAL: "LOCAL",
  FOLDER: "FOLDER",
} as const;

type PendingExportActionType =
  (typeof PENDING_EXPORT_ACTION)[keyof typeof PENDING_EXPORT_ACTION];

export const VarianceReportDownload = ({
  reportType,
  filter,
}: {
  reportType: ReportsType;
  filter: ReportFilterType;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const categoriesModal = useModal();
  const selectFolderModal = useModal();
  const [pendingAction, setPendingAction] =
    useState<PendingExportActionType | null>(null);
  const [confirmedCategoryUuids, setConfirmedCategoryUuids] = useState<
    string[]
  >([]);

  const [exportReport, { isLoading: isExporting }] =
    useExportVarianceExplanationReportMutation();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const period = `${dayjs(filter.START_DATE).format(DISPLAY_DATE)} - ${dayjs(
    filter.END_DATE
  ).format(DISPLAY_DATE)}`;

  const runExport = async ({
    categoryUuids,
    exportToMail,
    folderId,
  }: {
    categoryUuids: string[];
    exportToMail?: boolean;
    folderId?: string;
  }) => {
    try {
      const { document_id } = await exportReport({
        groupId,
        entityId,
        reportType,
        startDate: filter.START_DATE || null,
        endDate: filter.END_DATE || null,
        accountingMethod: filter.ACCOUNTING_METHOD,
        comparisonReportType: filter.COMPARE_BY,
        showNonZeroAccounts: filter.HIDE_ZERO,
        categoryUuidsForVendorTab: categoryUuids,
        folderId,
        exportToMail,
      }).unwrap();

      if (folderId) {
        successToast({ message: "Report saved in Inkle Documents" });
        return;
      }

      if (exportToMail) {
        successToast({ message: "Report will be sent to your email" });
        return;
      }

      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();
      openLink(download_url);
      successToast({ message: "Report downloaded" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const openCategoriesModalFor = (action: PendingExportActionType) => {
    setPendingAction(action);
    categoriesModal.open();
  };

  const handleCategoriesConfirmed = async (categoryUuids: string[]) => {
    if (pendingAction === PENDING_EXPORT_ACTION.FOLDER) {
      setConfirmedCategoryUuids(categoryUuids);
      categoriesModal.close();
      selectFolderModal.open();
      return;
    }

    await runExport({
      categoryUuids,
      exportToMail: pendingAction === PENDING_EXPORT_ACTION.MAIL,
    });
    categoriesModal.close();
    setPendingAction(null);
  };

  return (
    <>
      <Dropdown.Root>
        <Dropdown.Trigger asChild className="t-group">
          <div>
            <Button
              customType="primary"
              size="small"
              onClick={(e) => e.stopPropagation()}
              isLoading={isExporting}
              disabled={isExporting}
            >
              <div className="t-flex t-items-center t-gap-1">
                Export
                <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                  <ArrowDown color="currentColor" strokeWidth="1.5" size="16" />
                </div>
              </div>
            </Button>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content sideOffset={4} align="end" side="bottom">
            <ExportOptions
              onLocalDownload={() =>
                openCategoriesModalFor(PENDING_EXPORT_ACTION.LOCAL)
              }
              onMailExport={() =>
                openCategoriesModalFor(PENDING_EXPORT_ACTION.MAIL)
              }
              onSaveInInkleDocs={() =>
                openCategoriesModalFor(PENDING_EXPORT_ACTION.FOLDER)
              }
            />
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      {categoriesModal.isOpen && (
        <ExportFluxAnalysisModal
          isOpen={categoriesModal.isOpen}
          onClose={() => {
            categoriesModal.close();
            setPendingAction(null);
          }}
          isSubmitting={isExporting}
          period={period}
          onConfirm={(categoryUuids) => {
            handleCategoriesConfirmed(categoryUuids);
          }}
        />
      )}

      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={() => {
            selectFolderModal.close();
            setPendingAction(null);
          }}
          onUpload={(folderId) => {
            runExport({
              categoryUuids: confirmedCategoryUuids,
              folderId,
            });
            selectFolderModal.close();
            setPendingAction(null);
          }}
        />
      )}
    </>
  );
};
